import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { QrCode, Loader2, Camera, Search, Keyboard } from 'lucide-react';

const ScannerPage = () => {
  const navigate = useNavigate();
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState('');
  const codeInputRef = useRef(null);
  const isProcessingRef = useRef(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const html5QrcodeRef = useRef(null); // fallback instance for Safari/Firefox
  const fallbackContainerId = 'qr-scanner-fallback'; // div id used by html5-qrcode
  const [cameraActive, setCameraActive] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const stopCamera = useCallback(() => {
    // Tier 1 cleanup (native BarcodeDetector)
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    // Tier 2 cleanup (html5-qrcode fallback)
    if (html5QrcodeRef.current) {
      const inst = html5QrcodeRef.current;
      html5QrcodeRef.current = null;
      try {
        const p = inst.stop();
        if (p && typeof p.then === 'function') {
          p.then(() => { try { inst.clear(); } catch { /* ignore */ } })
           .catch(() => { /* already stopped */ });
        }
      } catch { /* ignore */ }
    }
    setUsingFallback(false);
    setCameraActive(false);
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  // Auto-focus the code input
  useEffect(() => {
    if (!cameraActive && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [cameraActive]);

  // === Process code: extract member_code, then redirect to /s/{code} ===
  // The InstantScanPage at /s/{code} calls /api/scan/lookup which returns ONLY
  // the cards the customer is enrolled on, so the merchant always picks the
  // correct card — no more silent scan on the wrong card.
  const processCode = useCallback((code) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setError('');
    stopCamera();

    const cleanCode = code.trim();

    // Extract embedded member_code if the input contains an LP-XXXXXX pattern.
    // Covers the case where a merchant pastes the full pass QR URL
    // (https://.../s/LP-XXXXXX?mid=...) into the manual "Code membre" field.
    // Format from services/auth.py generate_member_code: "LP-" + 6 alphanumerics
    // (6 digits in the nominal path, 6 uppercase hex in the collision fallback).
    const extractMatch = cleanCode.match(/LP-[A-Z0-9]{6}/i);
    const workingCode = extractMatch ? extractMatch[0] : cleanCode;

    let memberCode = '';

    if (workingCode.match(/^LP-/i)) {
      memberCode = workingCode.toUpperCase();
    } else if (workingCode.length > 20) {
      // Likely a UUID customer_id → not supported by /s/{code}, surface error
      setError("Format invalide. Saisissez le code membre LP-XXXXXX du client.");
      isProcessingRef.current = false;
      return;
    } else {
      // Short code without prefix
      memberCode = workingCode.toUpperCase().startsWith('LP') ? workingCode.toUpperCase() : `LP-${workingCode}`;
    }

    navigate(`/s/${memberCode}`);
  }, [stopCamera, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (codeInput.trim()) processCode(codeInput.trim());
  };

  // === Helper: handle a successful scan from either tier ===
  const handleScanSuccess = useCallback((rawValue) => {
    if (isProcessingRef.current) return;
    const match = rawValue.match(/\/s\/(LP-\w+)/i);
    processCode(match ? match[1] : rawValue);
  }, [processCode]);

  // === Tier 2 fallback: html5-qrcode (Safari iOS, Firefox, older browsers) ===
  const startCameraFallback = async () => {
    try {
      const mod = await import('html5-qrcode');
      const Html5Qrcode = mod.Html5Qrcode;
      // Wait one tick so the React-rendered <div id="..."> exists in the DOM
      await new Promise((r) => setTimeout(r, 50));
      const instance = new Html5Qrcode(fallbackContainerId, { verbose: false });
      html5QrcodeRef.current = instance;
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => handleScanSuccess(decoded),
        () => { /* per-frame decode failures are noise — ignore */ }
      );
    } catch (err) {
      console.error('html5-qrcode fallback failed:', err);
      setUsingFallback(false);
      setCameraActive(false);
      setError("Impossible d'ouvrir la caméra. Veuillez utiliser la saisie du code ci-dessus.");
    }
  };

  // === Camera scan — multi-tier: BarcodeDetector → html5-qrcode → manual ===
  const startCamera = async () => {
    setError('');
    isProcessingRef.current = false;

    // Tier 1: native BarcodeDetector (Chrome desktop/Android, Edge)
    if ('BarcodeDetector' in window) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setUsingFallback(false);
        setCameraActive(true);

        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const scanLoop = async () => {
          if (!streamRef.current || isProcessingRef.current) {
            animFrameRef.current = requestAnimationFrame(scanLoop);
            return;
          }
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0) {
              handleScanSuccess(barcodes[0].rawValue);
              return;
            }
          } catch {
            // BarcodeDetector frame scan failed - retry next frame
          }
          animFrameRef.current = requestAnimationFrame(scanLoop);
        };
        animFrameRef.current = requestAnimationFrame(scanLoop);
        return;
      } catch (err) {
        console.error('Camera (native) error:', err);
        setError("Impossible d'accéder à la caméra.");
        return;
      }
    }

    // Tier 2: html5-qrcode (Safari iOS 16+, Firefox, etc.)
    setUsingFallback(true);
    setCameraActive(true);
    await startCameraFallback();
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
          Scanner
        </h1>
        <p className="text-slate-600 mt-1">Saisissez le code membre ou scannez le QR du client</p>
        <p className="text-xs text-slate-400 mt-1">Vous choisirez ensuite la carte sur laquelle ajouter les points/tampons</p>
      </div>

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-center">
            <p className="text-red-700 font-medium" data-testid="scanner-error">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Main Scanner Input */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5" /> Code membre
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Big code input - PRIMARY METHOD */}
          <form onSubmit={handleSubmit}>
            <div className="flex gap-2">
              <Input
                ref={codeInputRef}
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder="LP-000000"
                className="h-14 text-center text-2xl font-mono tracking-wider border-2 border-slate-300 focus:border-blue-500"
                data-testid="member-code-input"
                autoComplete="off"
              />
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 h-14 px-6"
                disabled={!codeInput.trim()}
                data-testid="member-code-submit"
              >
                <Search className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs text-center text-slate-400 mt-2">
              Le code est affiché sur le pass Apple Wallet du client
            </p>
          </form>

          {/* Camera - secondary option */}
          <div className="border-t border-slate-100 pt-4">
            {cameraActive && (
              <div className="relative rounded-lg overflow-hidden bg-black mb-3">
                {usingFallback ? (
                  // Tier 2: html5-qrcode renders its own <video> inside this div
                  <div
                    id={fallbackContainerId}
                    className="w-full"
                    style={{ minHeight: '300px' }}
                    data-testid="qr-scanner-fallback"
                  />
                ) : (
                  // Tier 1: our own <video> element fed by getUserMedia
                  <video
                    ref={videoRef}
                    className="w-full"
                    playsInline
                    muted
                    style={{ maxHeight: '300px', objectFit: 'cover' }}
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-48 h-48 border-2 border-white/60 rounded-2xl" />
                </div>
                <button
                  onClick={stopCamera}
                  className="absolute top-3 right-3 bg-black/50 text-white rounded-full px-3 py-1 text-sm z-10"
                >
                  Fermer
                </button>
              </div>
            )}
            <Button
              onClick={cameraActive ? stopCamera : startCamera}
              variant="outline"
              className={`w-full h-11 ${cameraActive ? 'border-red-300 text-red-700' : 'border-slate-300 text-slate-600'}`}
              data-testid="start-camera-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              {cameraActive ? 'Fermer la caméra' : 'Scanner avec la caméra'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScannerPage;
